import React, { useState, useContext, useEffect } from 'react';
import { Alert } from 'antd';
import { useHistory } from 'react-router-dom';
import { SortOrder } from 'antd/lib/table';
import Spin from 'src/components/common/Spin';
import useQueryUrlParams from 'src/hooks/useQueryUrlParams';
import ProductSegment from '../types/ProductSegment';
import { OperationType } from '../types/SelectedOperation';
import { ColumnSorting } from './ProductSegmentTable/ProductSegmentTable.config';
import ProductSegmentTable from './ProductSegmentTable/ProductSegmentTable';
import useProductSegmentListFetch from './hooks/useProductSegmentListFetch';
import useProductSegmentFieldChange from './hooks/useProductSegmentFieldChange';
import useProductSegmentRemove from './hooks/useProductSegmentRemove';
import { useAppStateContext } from 'src/stateProviders/useAppStateContext';
import { ProductAlertsContext } from '../ProductAlertsProvider';
import styles from './ProductSegmentList.module.less';

export type ProductSegmentQueryParams = {
  sortingcolumn: keyof ProductSegment;
  sortingorder: SortOrder;
};

export const PAGE_NUMBER = 1;
export const PAGE_SIZE = 10;

const ProductSegmentList: React.FC = (): React.ReactElement => {
  const history = useHistory();
  const { user = null } = useAppStateContext();
  const readOnlyMode = user?.appPermissions['read_only_product_alert'];
  const { filters, forcedReload, reloadSegmentList, setSelectedOperation } = useContext(ProductAlertsContext);
  const { sortingcolumn = 'productSegmentName', sortingorder = 'ascend' }: ProductSegmentQueryParams =
    useQueryUrlParams();

  const [page, setPage] = useState(PAGE_NUMBER);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [sorting, setSorting] = useState<ColumnSorting>({ field: sortingcolumn, order: sortingorder });
  const [{ data, loading, error, total }] = useProductSegmentListFetch({
    filters,
    sorting,
    page,
    pageSize,
    forcedReload,
  });
  const firstLoading = loading && data === null;

  useEffect(() => {
    setPage(PAGE_NUMBER);
    setPageSize(PAGE_SIZE);
  }, [filters]);

  useEffect(() => {
    // go to the first page after deletion of the last product segment on the page
    if (data?.length === 0 && page > PAGE_NUMBER) {
      setPage(PAGE_NUMBER);
    }
  }, [data]);

  const [fieldChange, setProductSegmentField] = useProductSegmentFieldChange();
  const [removal, removeSegment] = useProductSegmentRemove();

  const handleFavoriteChange = (value: boolean, record: ProductSegment) => {
    if (readOnlyMode) return;
    const { productSegmentId } = record;
    setProductSegmentField({ productSegmentId, favourite: !value }, reloadSegmentList);
  };

  const handleAction = (productSegment: ProductSegment, type: OperationType) => {
    switch (type) {
      case OperationType.EditSegment:
      case OperationType.CloneSegment:
      case OperationType.ConfigureAssociatedProducts:
        return setSelectedOperation({ type, productSegment });

      case OperationType.DeleteSegment:
        return setSelectedOperation({
          type,
          productSegment,
          operation: () => removeSegment(productSegment.productSegmentId, reloadSegmentList),
        });

      case OperationType.CreateAlert:
        return history.push(`/productAlerts/alertManagement/${productSegment.productSegmentId}/new`);

      case OperationType.AlertManagement:
        return history.push(`/productAlerts/alertManagement/${productSegment.productSegmentId}`);
    }
  };

  return (
    <div>
      {(error || fieldChange.error || removal.error) && (
        <Alert
          className={styles.errorAlert}
          message="Error"
          description="Something went wrong. Please try again later..."
          type="error"
          showIcon
        />
      )}
      {firstLoading ? (
        <div className={styles.listLoader}>
          <Spin spinning={firstLoading} />
        </div>
      ) : (
        data && (
          <ProductSegmentTable
            user={user}
            items={data}
            sorting={sorting}
            loading={loading || fieldChange.loading || removal.loading}
            page={page}
            pageSize={pageSize}
            total={total}
            onSortingChange={setSorting}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onFavoriteChange={handleFavoriteChange}
            onAction={handleAction}
          />
        )
      )}
    </div>
  );
};

export default ProductSegmentList;
